import React, { useEffect, useState, useCallback, memo, useRef } from 'react';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Question } from 'enrollment/features/Questionnaire/components';
import { GET_DEPENDENTS_QUERY_KEY } from 'enrollment/queries';
import {
  BenefitDto,
  CategoryResponseDto,
  QuestionDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import {
  api,
  queryClient,
  useApiMutation,
  queryKeyGenerator,
} from 'theSource/utils';
import {
  selectAnsweredSurveyQuestions,
  setAnsweredSurveyQuestionsData,
} from 'enrollment/services';
import { CoverageDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  findQuestion,
  SUB_ONLY_TYPE,
  MULTI_CHOICE_SURVEY_TYPE,
  MULTI_SELECT_SURVEY_TYPE,
  mapQuestionsToBenefits,
  removeBenefitsThatAreRestricted,
  hideSurveyModal,
} from 'enrollment/features/Questionnaire/utils';
import { mapRequestData } from 'enrollment/features/Questionnaire/components/QuestionnaireForm';
import {
  QuestionnaireRequestDto,
  UserResponseDto,
} from 'enrollment/features/Questionnaire/types';
import { PUT_SURVEY_RESPONSES_QUERY_KEY } from 'enrollment/queries/useBenefitCategoryQuery/utils';
import { ResponseDto, ErrorResponseDto } from 'theSource/types';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgLifeEventId,
  selectEnrollmentOrgPlanId,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useConfigurationQuery } from 'theSource/queries';
import { COVERAGES_BENEFIT_CATEGORY } from 'enrollment/queries/useBenefitCartQuery/utils';
import { PageContentHeaderDisplay } from '../PageContentHeaderDisplay';
import { EsignatureModal } from '../../features/Plans/EsignatureModal';
import { getShowEsignModal } from '../../features/Plans/utils';
import { selectIsAdmin } from 'theSource/features';
import { AdditionalPlanElectionDataDto } from '../../features/Plans/PlanCard/types';
import { openEsignatureModal } from '../../features/Plans/EsignatureModal/tests/utils';
import { useRestrictionsQuery } from 'enrollment/queries/useRestrictionsQuery';
import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';
import { ToggleBooleanState } from 'components/ToggleBooleanState';
import { PsModalTuner } from 'stencilSetup';

declare const bootstrap: any;

interface SurveyPopupProps {
  categoryResponse: CategoryResponseDto | undefined;
  currentCategory: string;
}

interface ValidatedUserResponse extends UserResponseDto {
  isValid?: boolean;
}

export const SurveyPopup = memo(function SurveyPopup({
  categoryResponse,
  currentCategory,
}: SurveyPopupProps) {
  const { t } = useTranslation('pages.questionnaire');
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsAdmin);

  const [esignedBenefits, setEsignedBenefits] = useState<number[]>([]);
  const [responses, setResponses] = useState<ValidatedUserResponse[]>([]);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState<QuestionDto[]>(
    []
  );
  const [esignatureCount, setEsignatureCount] = useState(0);
  const [allSubmitted, setAllSubmitted] = useState<boolean[]>([]);
  const [requestLength, setRequestLength] = useState(0);
  // const [isOpen, setIsOpen] = useState(false);
  const answeredQuestions = useSelector(selectAnsweredSurveyQuestions);
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const { data: dependentsData } = useQuery<DependentsResponseDto>(
    GET_DEPENDENTS_QUERY_KEY,
    () => api.get<DependentsResponseDto>('/dependents')
  );

  const [saveButtonDisabled, setSaveButtonDisabled] = ToggleBooleanState(false);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = ToggleBooleanState(false);
  
  const questionnaireForm = 'questionnaire-form';

  let questionnaireResponse = categoryResponse as CategoryResponseDto;

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const mappedQuestionToBenefitIds = mapQuestionsToBenefits(
    questionnaireResponse
  );

  const isPopupBenefit = useCallback((benefit: BenefitDto) => {
    if (benefit.benefit.benefit_lookup_code === 'survey_unum_eligibility') {
      return false;
    }
    return (
      (benefit.benefit.benefit_class === 'survey' &&
        Boolean(benefit.coverage)) || // prevent popup for surveys not yet viewed
      (benefit.benefit.org_benefit_category === 'survey' &&
        !Boolean(benefit.coverage))
    );
  }, []); // No dependencies since it only uses the passed parameter

  const loadSurveyBenefits = useCallback(() => {
    return questionnaireResponse?.data.benefits.filter((benefit) => {
      return (
        isPopupBenefit(benefit) &&
        benefit.org_benefit_survey_questions?.some((question) => {
          return unansweredQuestions.includes(question);
        })
      );
    });
  }, [questionnaireResponse, unansweredQuestions, isPopupBenefit]);

  const getActuallyRenderedQuestionIds = useCallback(() => {
    const ids = new Set<number>();
    const surveyBenefits = loadSurveyBenefits();

    if (!surveyBenefits) { return ids; };

    unansweredQuestions?.forEach(question => {
      // Only include if mapped to this benefit
      const isMapped = surveyBenefits?.some(benefit =>
        mappedQuestionToBenefitIds
          .get(benefit.benefit.org_benefit_id)
          ?.includes(question.question_id)
      );

      // Check parent/trigger logic (if any)
      let isVisible = true;
      if (question.parent_question_id !== undefined) {
        const parentResponse = responses.find(
          r => r.question_id === question.parent_question_id
        );

        isVisible = Boolean(
          parentResponse &&
          question.trigger_answers?.some(answer =>
            parentResponse.response.includes(answer)
          )
        );
      }

      if (isMapped && isVisible) {
        ids.add(question.question_id);
      }
    });

    return ids;
  }, [loadSurveyBenefits, unansweredQuestions, mappedQuestionToBenefitIds, responses]);

  const renderedQuestionIds = getActuallyRenderedQuestionIds();

  const updateSaveButtonState = useCallback((responses: ValidatedUserResponse[], questions: QuestionDto[]) => {
    // Get all visible required questions
    const visibleRequiredQuestions = questions.filter(q => {
      // Must be required
      if (q.is_optional) {
        return false;
      }
      
      // Must be visible
      const renderedIds = getActuallyRenderedQuestionIds();
      if (!renderedIds.has(q.question_id)) {
        return false;
      }
      
      // Handle dependent fields
      if (q.parent_question_id !== undefined) {
        const parentResponse = responses.find(r => r.question_id === q.parent_question_id);
        return Boolean(parentResponse?.response.some(r => 
          q.trigger_answers?.includes(r)
        ));
      }
      
      return true;
    });

    // All required fields must have valid responses
    const allRequiredValid = visibleRequiredQuestions.every(q => {
      const response = responses.find(r => r.question_id === q.question_id);
      const hasResponse = Boolean(response?.response?.length);
      const isValidResponse = response?.isValid !== false;
      
      return hasResponse && isValidResponse;
    });

    setSaveButtonDisabled(!allRequiredValid);
  }, [getActuallyRenderedQuestionIds, setSaveButtonDisabled]);

  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContextType === 'life_event' ? orgPlanYearContextId : 0;
  const orgLifeEventID: number = useSelector(selectEnrollmentOrgLifeEventId);
  const { data: restrictions } = useRestrictionsQuery({
    orgLifeEventID: orgLifeEventID,
    orgPlanYearID: orgPlanYearId,
    enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
  });

  const toast = useToastDialog();

  if (
    restrictions &&
    categoryResponse &&
    enrollmentContextType === 'life_event'
  ) {
    questionnaireResponse = removeBenefitsThatAreRestricted(
      restrictions.data,
      categoryResponse as CategoryResponseDto
    )[0];
  }

  const filterEsignData = (benefit: BenefitDto) => {
    // this is just for survey benefits so they should all have plans data
    const planEsignData = benefit.plans?.[0]?.esign_data;
    const esignData = {
      ...benefit.coverage?.esign_data,
      ...benefit.plans?.[0]?.esign_data,
      esign_text: planEsignData?.esign_text,
      custom_text: planEsignData?.custom_text,
      admin_esign_text: planEsignData?.admin_esign_text,
      esigned: false,
    };
    const orgBenefitId = benefit.benefit.org_benefit_id;
    // TODO: set esigned to false temporary to show the esignature modal if survey question comes in survey popup that requires esign
    // TODO: This will be removed once election changes requires esignature handled in api response

    const showEsignatureModal = getShowEsignModal(
      esignData,
      benefit?.coverage as unknown as CoverageDto,
      isAdmin,
      false
    );
    return { showEsignatureModal, orgBenefitId, esignData };
  };

  const notAllFollowups = (unansweredQuestions: QuestionDto[]) => {
    return !unansweredQuestions.every((question) => {
      if (question.parent_question_id === undefined) {
        return false;
      } else {
        return true;
      }
    });
  };

  useEffect(() => {
    let esignIncrement = 0;
    const esignConfiguredBenefits: Number[] = [];
    const surveyBenefits = loadSurveyBenefits();

    surveyBenefits?.forEach((benefit) => {
      let existingData = true;
      const { showEsignatureModal, orgBenefitId } = filterEsignData(benefit);
      if (
        showEsignatureModal &&
        !esignConfiguredBenefits.includes(orgBenefitId)
      ) {
        esignConfiguredBenefits.push(benefit.benefit.org_benefit_id);
        existingData = false;
      }
      if (!existingData) {
        esignIncrement++;
      }
    });
    setEsignatureCount(esignIncrement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unansweredQuestions]);

  const disableButton = () => {
    const allRequired = unansweredQuestions.filter((question) => {
      return !question.is_optional;
    });

    const allRequiredVisible = allRequired.filter((question) => {
      if (!renderedQuestionIds.has(question.question_id)) { return false; };
      if (question.parent_question_id === undefined) {
        return true;
      }
      const parentQuestionIndex = responses.findIndex((response) => {
        return response.question_id === question.parent_question_id;
      });
      if (parentQuestionIndex === -1) {
        return true;
      }
      return question.trigger_answers?.some((answer) => {
        return responses[parentQuestionIndex].response.includes(answer);
      });
    });

    if (allRequiredVisible.length === 0) {
      return false;
    }

    return !allRequiredVisible.every((question) => {
      const response = responses.find(
        (response) => response.question_id === question.question_id
      );
      return response ? response.response.length > 0 : false;
    });
  };

  useEffect(() => {
    if (unansweredQuestions.length > 0) {
      // Whenever questions change, update the save button state
      updateSaveButtonState(responses, unansweredQuestions);
    }
  }, [unansweredQuestions, responses, renderedQuestionIds, updateSaveButtonState]);

  const handleChange = (event: Event | React.SyntheticEvent) => {
    
    if (!event || !event.target) {
      return;
    }

    // Handle both native events and React synthetic events
    const target = event.target as any;
    if (!('value' in target)) {
      return;
    }

    // Use the custom isValid property from the event
    const isValid = target.isValid ?? true;

    let inputValue = target.value;
    
    // For checkboxes and radio buttons, update inputValue based on checked state
    if (target.type === 'checkbox' || target.type === 'radio') {
      inputValue = target.checked ? target.value : '';
    }

    // Get question ID from either id attribute or data attribute
    const questionId = target.id || target.getAttribute('data-input-id') || '';
    
    if (!questionId) {
      return;
    }

    // Extract question ID from format like "q21068292_r-1"
    const questionIdMatch = questionId.match(/q(\d+)_r(-?\d+)/);
    
    if (!questionIdMatch) {
      // Special handling for height fields
      if (questionId.startsWith('inch') || questionId.startsWith('ft')) {
        const subStr = questionId.substring(questionId.indexOf('-'));
        const heightFtInput = document.getElementById(`ft${subStr}`) as HTMLInputElement;
        const heightInInput = document.getElementById(`inch${subStr}`) as HTMLInputElement;

        if (!heightFtInput || !heightInInput) {
          return;
        }

        // Get the base question ID from one of the height fields
        const baseQuestionId = heightFtInput.getAttribute('data-base-question-id');
        if (!baseQuestionId) {
          return;
        }

        const feetValue = heightFtInput.value.trim();
        const inchesValue = heightInInput.value.trim();

        // Custom validation - ensure values are present and in valid range
        const feetValueNum = parseFloat(feetValue);
        const inchesValueNum = parseFloat(inchesValue);
        const feetValid = feetValue !== '' && !isNaN(feetValueNum) && feetValueNum >= 0 && feetValueNum <= 8;
        const inchesValid = inchesValue !== '' && !isNaN(inchesValueNum) && inchesValueNum >= 0 && inchesValueNum < 12;

        // Both fields must be valid for the height to be valid
        const heightValid = feetValid && inchesValid;

        // Calculate total height in inches
        const totalInches = (feetValueNum * 12) + inchesValueNum;

        // Update the responses array
        const newResponses = responses.map(response => {
          if (response.question_id.toString() === baseQuestionId) {
            return {
              ...response,
              response: heightValid ? [totalInches.toString()] : [],
              isValid: heightValid
            };
          }
          return response;
        });

        setResponses(newResponses);
        updateSaveButtonState(newResponses, unansweredQuestions);
        return;
      }
      
      return;
    }

    const questionIdFormatted = Number(questionIdMatch[1]);
    const responseIdFormatted = questionIdMatch[2];
    
    const question = findQuestion(questionIdFormatted, unansweredQuestions);
    if (!question) {
      return;
    }

    const newResponses = responses.map((response): ValidatedUserResponse => {
      if (response.question_id !== question.question_id) {
        return response;
      }
      
      // fill in useState array with new response value array
      if (inputValue === '') {
        if (question.targeted_at_type !== SUB_ONLY_TYPE) {
          return response;
        } else {
          return {
            question_id: response.question_id,
            response: [],
            isValid
          };
        }
      }

      if (target.checked || question.targeted_at_type !== SUB_ONLY_TYPE) {
        if (question.question_type === MULTI_CHOICE_SURVEY_TYPE) {
          // radio - we want to replace old value with new
          return {
            question_id: response.question_id,
            response: [responseIdFormatted],
            isValid
          };
        } else {
          return {
            question_id: response.question_id,
            response: [...response.response, responseIdFormatted],
            isValid
          };
        }
      } else {
        // will remove previous value from useState array
        let responseValue = [inputValue];
        if (question.question_type === MULTI_SELECT_SURVEY_TYPE) {
          responseValue = response.response.filter(
            (response) => response !== responseIdFormatted
          );
        }
        return {
          question_id: response.question_id,
          response: responseValue,
          isValid
        };
      }
    });

    setResponses(newResponses);
    updateSaveButtonState(newResponses, unansweredQuestions);
  };

  const { mutate: SurveyResponsesRequest, isLoading: isSubmitting } =
    useApiMutation<ResponseDto, ErrorResponseDto, QuestionnaireRequestDto>({
      queryKey: PUT_SURVEY_RESPONSES_QUERY_KEY,
      method: 'put',
      endpoint: `${lumosApiHost}/v2/self_service/benefits/survey`,
      showSuccessSnackbar: false,
      onSuccess: (data, requestVariables) => {
        if (
          requestVariables?.esign_checked ||
          requestVariables?.esign_password
        ) {
          const esignedBenefitData = esignedBenefits;
          esignedBenefitData.push(requestVariables.org_benefit_id);
          setEsignedBenefits(esignedBenefitData);
          setEsignatureCount(esignatureCount - 1);
        }
        setAllSubmitted((prev) => [...prev, true]);
      },
      onError: () => {
        setAllSubmitted((prev) => [...prev, false]);
      },
      toastRef: toast
    });

  const filteredRequestParameters = () => {
    const questionIds = responses.map((response) => response.question_id);
    return mapRequestData({
      questionnaireResponse: questionnaireResponse,
      enrollmentContextType: enrollmentContextType,
      questionIds: questionIds,
    });
  };

  const validateSurveyBenefitResponses = (
    surveyBenefitResponses: QuestionnaireRequestDto
  ) => {
    return surveyBenefitResponses.election.survey_responses.every(
      (response) => response === undefined
    );
  };

  const handleSubmit = () => {
    
    if (!categoryResponse) {
      return;
    }
    setIsSubmittingForm(true);
    let requestCount = 0;

    const requestParameters = filteredRequestParameters();
    requestParameters.forEach((surveyBenefitResponses) => {
      if (validateSurveyBenefitResponses(surveyBenefitResponses)) {
        return;
      } else {
        const benefit = categoryResponse?.data?.benefits.find(
          benefit => benefit?.benefit?.org_benefit_id === surveyBenefitResponses.org_benefit_id
        );

        const { showEsignatureModal, esignData } = filterEsignData(
          benefit as unknown as BenefitDto
        );

        if (esignData && showEsignatureModal) {
          if (
            !esignedBenefits.includes(surveyBenefitResponses.org_benefit_id)
          ) {
            openEsignatureModal(surveyBenefitResponses.org_benefit_id);
          }
        } else {
          requestCount++;
          SurveyResponsesRequest(surveyBenefitResponses);
          closeModal(() => setIsSurveyModalOpen(false));
        }
      }
    });
    setRequestLength(requestCount);
  };

  const handleElection = (
    additionalPlanData?: AdditionalPlanElectionDataDto
  ) => {
    if (!categoryResponse) {
      return;
    }
    let requestCount = requestLength;
    const requestParameters = filteredRequestParameters();
    requestParameters.forEach((surveyBenefitResponses) => {
      if (validateSurveyBenefitResponses(surveyBenefitResponses)) {
        return;
      } else {
        if (
          surveyBenefitResponses.org_benefit_id === additionalPlanData?.plan_id
        ) {
          if (additionalPlanData?.esign_checked) {
            surveyBenefitResponses.esign_checked =
              additionalPlanData.esign_checked;
          }
          if (additionalPlanData?.esign_password) {
            surveyBenefitResponses.esign_password =
              additionalPlanData.esign_password;
          }
          SurveyResponsesRequest(surveyBenefitResponses);
          requestCount++;
        }
      }
    });

    setRequestLength(requestCount);
  };

  // useEffect to refresh benefits once all survey endpoints succeed
  useEffect(() => {
    if (
      allSubmitted.length > 0 &&
      allSubmitted.length === requestLength &&
      esignatureCount === 0 &&
      !isSubmitting
    ) {
      if (allSubmitted.every((submitted) => submitted)) {
        if (esignatureCount === 0) {
          const allAnsweredQuestions = [
            ...answeredQuestions,
            ...responses.map((response) => response.question_id),
          ];
          dispatch(
            setAnsweredSurveyQuestionsData({ values: allAnsweredQuestions })
          );
          setUnansweredQuestions([]);
          closeModal(() => setIsSurveyModalOpen(false));
        }
        hideSurveyModal();
        queryClient.invalidateQueries(
          queryKeyGenerator(
            `benefits-${enrollmentContextType}`,
            Number(orgPlanYearContextId)
          )
        );
        queryClient.invalidateQueries(
          queryKeyGenerator(
            `${currentCategory}-${enrollmentContextType}`,
            Number(orgPlanYearContextId)
          )
        );
        queryClient.invalidateQueries(
          queryKeyGenerator(
            `contribution-${enrollmentContextType}`,
            Number(orgPlanYearContextId)
          )
        );
        queryClient.invalidateQueries(
          queryKeyGenerator(
            COVERAGES_BENEFIT_CATEGORY,
            Number(orgPlanYearContextId)
          )
        );
      }

      setRequestLength(0);
      setAllSubmitted([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allSubmitted,
    currentCategory,
    enrollmentContextType,
    requestLength,
    esignatureCount,
  ]);

  // useEffect to check benefits response after each election
  const [hasProcessedInitialBenefits, setHasProcessedInitialBenefits] = useState(false);

  useEffect(() => {    
    // Don't process if we've already handled the initial state or modal is open
    if (hasProcessedInitialBenefits || isSurveyModalOpen) {
      return;
    }

    if (categoryResponse && categoryResponse.data.benefits) {
      let filteredBenefits = categoryResponse;
      if (
        restrictions &&
        filteredBenefits &&
        enrollmentContextType === 'life_event'
      ) {
        filteredBenefits = removeBenefitsThatAreRestricted(
          restrictions.data,
          filteredBenefits
        )[0];
      }

      const surveyBenefits = filteredBenefits.data.benefits.filter(
        (benefit) => {
          return isPopupBenefit(benefit);
        }
      );

      const unansweredArr = surveyBenefits.flatMap(
        (benefit) =>
          benefit.org_benefit_survey_questions?.filter(
            (question) =>
              !answeredQuestions.includes(question.question_id) &&
              (!Boolean(benefit.coverage) ||
                benefit.coverage?.survey_responses?.some(
                  (response) => response.question_id === question.question_id
                ) === false)
          ) as QuestionDto[]
      );

      // added if statement to fix spec test infinite loop on benefitsPreview.spec.tsx
      if (unansweredArr.length > 0) {
        
        // Only update if the questions have actually changed
        if (JSON.stringify(unansweredArr) !== JSON.stringify(unansweredQuestions)) {
          // Initialize responses with validation state
          const initialResponses = unansweredArr.map((question) => {
            const response: ValidatedUserResponse = {
              question_id: question.question_id,
              response: [],
              isValid: question.is_optional // Optional fields start as valid
            };
            return response;
          });
          
          setResponses(initialResponses);
          setUnansweredQuestions(unansweredArr);

          // Initial button state
          const visibleRequiredQuestions = unansweredArr.filter(q => {
            if (q.is_optional) {
              return false;
            }
            return true;
          });

          const hasRequiredFields = visibleRequiredQuestions.length > 0;
          
          // Trigger validation via our helper
          if (hasRequiredFields) {
            updateSaveButtonState(initialResponses, unansweredArr);
          } else {
            setSaveButtonDisabled(false); // Enable if no required fields
          }
          
          // Mark that we've processed the initial benefits
          setHasProcessedInitialBenefits(true);
        }
      }
    }
    // eslint-disable-next-line
  }, [
    enrollmentContextType,
    categoryResponse,
    answeredQuestions,
    restrictions,
    isSurveyModalOpen,
    hasProcessedInitialBenefits
  ]);

  const openModal = useCallback(() => {    
    // Prevent opening if already open or submitting
    if (isSurveyModalOpen || isSubmittingForm) {
      return;
    }

    const modalElement = document.getElementById(questionnaireForm);
    if (!modalElement) {
      return;
    }

    const showModal = () => {
      try {
        if (!modalInstanceRef.current) {
          modalInstanceRef.current = new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: true
          });
        }
        modalInstanceRef.current.show();
        setIsSurveyModalOpen(true);
      } catch (err) {
        // Silently handle error
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    if (document.readyState === 'complete') {
      showModal();
    } else {
      window.requestAnimationFrame(showModal);
    }
  // eslint-disable-next-line
  }, [questionnaireForm,
      setIsSurveyModalOpen,
      isSurveyModalOpen,
      unansweredQuestions,
      isSubmittingForm]
  );

  const closeModal = useCallback((callback: () => void) => {
    
    if (modalInstanceRef.current) {
      const modalElement = document.getElementById(questionnaireForm);
      if (modalElement) {
        modalElement.addEventListener('hidden.bs.modal', () => {
          callback();
        }, { once: true });
        
        try {
          modalInstanceRef.current.hide();
        } catch (err) {
          // Force callback if hide fails
          callback();
        }
      }
    } else {
      callback();
    }
  }, [questionnaireForm]);

  // useEffect to open the modal when new questions are available
  // from already visited benefits
  useEffect(() => {
    // Skip if not ready or already processed
    if (!categoryResponse || !mountedRef.current || !hasProcessedInitialBenefits) {
      return;
    }
    
    // Only proceed if we have the right conditions
    if (
      unansweredQuestions.length > 0 &&
      notAllFollowups(unansweredQuestions) &&
      !isSurveyModalOpen &&
      !isSubmittingForm &&
      mountedRef.current
    ) {
      // Use a timeout to ensure state updates have settled
      const timeoutId = setTimeout(() => {
        if (mountedRef.current && !isSurveyModalOpen) {
          openModal();
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [
    unansweredQuestions,
    isSurveyModalOpen,
    openModal,
    isSubmittingForm,
    categoryResponse,
    hasProcessedInitialBenefits
  ]);

  const renderBody = useCallback(() => {
    const surveyBenefits = loadSurveyBenefits();

    return (
      <div>
        {surveyBenefits?.map((benefit, index) => {
          return (
            <div
              key={index}
              className={
                index < surveyBenefits.length - 1 ? 'border-bottom pb-5' : ''
              }
            >
              <PageContentHeaderDisplay
                benefit={benefit.benefit}
                lumosApiHost={lumosApiHost}
                displayPageHeader={false}
                pageContentLookupCode="ENROLL_COVERAGE"
                orgPlanYearId={benefit.benefit.org_plan_year_id}
              />
              {unansweredQuestions.map(
                (question) =>
                  mappedQuestionToBenefitIds
                    .get(benefit.benefit.org_benefit_id)
                    ?.includes(question.question_id) && (
                    <Question
                      key={`q_${question.question_id}`}
                      question={question}
                      responses={responses}
                      questionResponse={categoryResponse}
                      flattenedData={unansweredQuestions}
                      dependents={dependentsData?.data.dependents}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      isPopup={true}
                    />
                  )
              )}
            </div>
          );
        })}
      </div>
    );
  // eslint-disable-next-line
  }, [loadSurveyBenefits,
      unansweredQuestions,
      mappedQuestionToBenefitIds,
      responses,
      lumosApiHost,
      dependentsData?.data.dependents,
      categoryResponse,
      isSubmitting]
  );

  const renderEsignatureModals = () => {
    const esignConfiguredBenefits: Number[] = [];
    const surveyBenefits = loadSurveyBenefits();
    return (
      <div>
        {surveyBenefits?.map((benefit) => {
          let existingData = true;
          const { showEsignatureModal, orgBenefitId, esignData } =
            filterEsignData(benefit);
          const carrierLogoUrl =
            benefit.coverage?.carrier_logo_url ||
            benefit.plans?.[0]?.carrier_logo_url;
          const carrierName =
            benefit.coverage?.carrier_name || benefit.plans?.[0]?.carrier;
          if (
            showEsignatureModal &&
            !esignConfiguredBenefits.includes(orgBenefitId)
          ) {
            esignConfiguredBenefits.push(orgBenefitId);
            existingData = false;
          }
          return (
            <div key={benefit.benefit.org_benefit_id}>
              {showEsignatureModal && !existingData && (
                <EsignatureModal
                  setEsignElectionData={handleElection}
                  carrierLogoUrl={carrierLogoUrl}
                  carrierName={carrierName}
                  data={esignData}
                  planId={orgBenefitId}
                  hideChoosePlanButton={true}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Add component lifecycle and modal handling
  const mountedRef = useRef(true);
  const modalInstanceRef = useRef<any>(null);
  
  // Effect for component lifecycle
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Separate effect for modal initialization
  useEffect(() => {
    if (!mountedRef.current) {
      return;
    }

    // Clean up any existing modal instance first
    if (modalInstanceRef.current) {
      try {
        modalInstanceRef.current.dispose();
      } catch (err) {
        // Silently handle error
      }
      modalInstanceRef.current = null;
    }

    // Initialize modal
    const modalElement = document.getElementById(questionnaireForm);
    if (modalElement) {
      try {
        modalInstanceRef.current = new bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: true
        });
      } catch (err) {
        // Silently handle error
      }
    }

    return () => {
      if (modalInstanceRef.current) {
        try {
          modalInstanceRef.current.dispose();
        } catch (err) {
          // Silently handle error
        }
        modalInstanceRef.current = null;
      }
    };
  }, [questionnaireForm]);

  return (
    <>
      <PsModalTuner
        data-testid="survey-unanswered-modal"
        modalId={questionnaireForm}
        modalTitle={t('modalTitle')}
        primaryButton="save"
        modalStatic={true}
        modalKeyboard={true}
        disablePrimary={disableButton() || isSubmitting || saveButtonDisabled}
        onPsModalSubmit={() => handleSubmit()}
        onPsModalDismiss={() => closeModal(() => setIsSurveyModalOpen(false))}
      >
        {renderBody()}
      </PsModalTuner>
      {renderEsignatureModals()}
    </>
  );
});
